import jsPDF from "jspdf";
import { Ticket as TicketType, TicketResposta, TicketTimelineEvent } from "@/types";
import toast from "react-hot-toast";

/**
 * Exporta a conversa do ticket para PDF
 */
export const exportTicketToPDF = (
  ticket: TicketType,
  timeline: TicketTimelineEvent[] = []
) => {
  if (!ticket) return;

  try {
    toast.loading("Gerando PDF...", { id: "pdf-export" });

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = margin;

    // Função auxiliar para formatar data de forma simples
    const formatDateSimple = (dateString: string) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    // Função auxiliar para adicionar nova página se necessário
    const checkPageBreak = (requiredHeight: number) => {
      if (yPosition + requiredHeight > pageHeight - margin - 12) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Função para adicionar texto com quebra de linha
    const addText = (
      text: string,
      fontSize: number,
      isBold: boolean = false,
      color: string = "#000000",
      lineHeight: number = 4
    ) => {
      doc.setFontSize(fontSize);
      doc.setTextColor(color);
      if (isBold) {
        doc.setFont(undefined, "bold");
      } else {
        doc.setFont(undefined, "normal");
      }

      const lines = doc.splitTextToSize(text, maxWidth);
      lines.forEach((line: string) => {
        checkPageBreak(lineHeight);
        doc.text(line, margin, yPosition);
        yPosition += lineHeight;
      });
    };

    // Cabeçalho moderno e profissional
    const headerHeight = 30;

    // Gradiente simulado (camadas de azul)
    doc.setFillColor(37, 99, 235); // Azul escuro
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    doc.setFillColor(59, 130, 246); // Azul médio
    doc.rect(0, 0, pageWidth, headerHeight - 3, "F");

    // Linha decorativa inferior
    doc.setFillColor(29, 78, 216); // Azul mais escuro
    doc.rect(0, headerHeight - 1, pageWidth, 1, "F");

    // Container interno com padding
    const headerPadding = 12;
    const headerContentY = headerPadding;

    // Ícone/Tag visual (retângulo arredondado)
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, headerContentY - 3, 45, 8, 2, 2, "FD");
    doc.setTextColor(59, 130, 246);
    doc.setFontSize(8);
    doc.setFont(undefined, "bold");
    doc.text("TICKET", margin + 3, headerContentY + 2);

    // Número do ticket (grande e destacado)
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont(undefined, "bold");
    const ticketNumber = `#${ticket.id}`;
    doc.text(ticketNumber, margin + 50, headerContentY + 6);

    // Informações adicionais no header (alinhadas à direita)
    const rightSideX = pageWidth - margin - 5;
    let rightSideY = headerContentY;

    if (ticket.created_at) {
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.setTextColor(200, 220, 255); // Branco suave
      const createdText = formatDateSimple(ticket.created_at);
      const createdWidth = doc.getTextWidth(createdText);
      doc.text(createdText, rightSideX - createdWidth, rightSideY);
      rightSideY += 4;
    }

    // Status badge no header (se disponível)
    if (ticket.status_nome) {
      const statusColors: { [key: string]: number[] } = {
        Fechado: [34, 197, 94],
        "Em Andamento": [59, 130, 246],
        Aberto: [245, 158, 11],
      };
      const statusColor = statusColors[ticket.status_nome] || [107, 114, 128];

      doc.setFontSize(7);
      doc.setFont(undefined, "bold");
      const statusText = ticket.status_nome.toUpperCase();
      const statusTextWidth = doc.getTextWidth(statusText);
      const badgePadding = 6;
      const badgeHeight = 6;
      const badgeWidth = statusTextWidth + badgePadding * 2;
      const badgeX = rightSideX - badgeWidth;
      const badgeY = rightSideY - 1;

      // Badge background
      doc.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
      doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 1.5, 1.5, "F");

      // Texto do badge (centralizado)
      doc.setTextColor(255, 255, 255);
      doc.text(statusText, badgeX + badgePadding, badgeY + 4);
    }

    yPosition = headerHeight + 8;

    // Box de informações do ticket melhorado
    const boxY = yPosition;
    const boxPadding = 4;
    const boxHeight = 42;

    // Sombra sutil
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin + 1, boxY + 1, maxWidth, boxHeight, 3, 3, "F");

    // Box principal
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, boxY, maxWidth, boxHeight, 3, 3, "FD");

    yPosition += boxPadding;

    // Assunto com destaque melhorado
    doc.setFontSize(11);
    doc.setTextColor(107, 114, 128); // Cinza médio
    doc.setFont(undefined, "normal");
    doc.text("Assunto", margin + boxPadding, yPosition);

    yPosition += 5;

    doc.setFontSize(12);
    doc.setTextColor(31, 41, 55); // Preto
    doc.setFont(undefined, "bold");
    const assuntoLines = doc.splitTextToSize(
      ticket.assunto || "Não especificado",
      maxWidth - boxPadding * 2
    );
    doc.text(assuntoLines, margin + boxPadding, yPosition);
    yPosition += assuntoLines.length * 4.5 + 3;

    // Linha separadora
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.line(
      margin + boxPadding,
      yPosition,
      pageWidth - margin - boxPadding,
      yPosition
    );
    yPosition += 4;

    // Informações do ticket em duas colunas melhoradas
    doc.setFontSize(8);
    const leftCol = margin + boxPadding;
    const rightCol = margin + maxWidth / 2 + 5;
    const lineSpacing = 4.5;

    // Função auxiliar para adicionar label + valor
    const addInfoRow = (
      label: string,
      value: string,
      col: number,
      y: number,
      isBold: boolean = false
    ) => {
      const xPos = col === 0 ? leftCol : rightCol;
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128); // Cinza
      doc.setFont(undefined, "normal");
      doc.text(`${label}:`, xPos, y);

      const labelWidth = doc.getTextWidth(`${label}: `);
      doc.setFontSize(8);
      doc.setTextColor(31, 41, 55); // Preto
      if (isBold) {
        doc.setFont(undefined, "bold");
      } else {
        doc.setFont(undefined, "normal");
      }
      doc.text(value, xPos + labelWidth, y);
    };

    // Status com cor baseada no status
    const statusColor =
      ticket.status_nome === "Fechado"
        ? [34, 197, 94] // Verde
        : ticket.status_nome === "Em Andamento"
        ? [59, 130, 246] // Azul
        : [107, 114, 128]; // Cinza

    // Desenhar círculo ANTES do texto
    const statusLabelX = leftCol;
    const statusLabelText = "Status:";
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.setFont(undefined, "normal");
    doc.text(statusLabelText, statusLabelX, yPosition);

    const statusLabelWidth = doc.getTextWidth(statusLabelText + " ");
    const statusValue = ticket.status_nome || "N/A";
    const circleX = statusLabelX + statusLabelWidth;
    const circleY = yPosition;

    // Desenhar círculo
    doc.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.circle(circleX + 1.5, circleY - 1.5, 1.5, "F");

    // Desenhar valor após o círculo
    doc.setFontSize(8);
    doc.setTextColor(31, 41, 55);
    doc.setFont(undefined, "bold");
    doc.text(statusValue, circleX + 5, yPosition);

    // Prioridade com cor baseada na prioridade
    const prioridadeColor =
      ticket.prioridade_nome === "Alta" || ticket.prioridade_nome === "Urgente"
        ? [239, 68, 68] // Vermelho
        : ticket.prioridade_nome === "Média"
        ? [245, 158, 11] // Laranja
        : [107, 114, 128]; // Cinza

    // Desenhar círculo ANTES do texto
    const prioridadeLabelX = rightCol;
    const prioridadeLabelText = "Prioridade:";
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.setFont(undefined, "normal");
    doc.text(prioridadeLabelText, prioridadeLabelX, yPosition);

    const prioridadeLabelWidth = doc.getTextWidth(prioridadeLabelText + " ");
    const prioridadeValue = ticket.prioridade_nome || "N/A";
    const prioridadeCircleX = prioridadeLabelX + prioridadeLabelWidth;

    // Desenhar círculo
    doc.setFillColor(prioridadeColor[0], prioridadeColor[1], prioridadeColor[2]);
    doc.circle(prioridadeCircleX + 1.5, circleY - 1.5, 1.5, "F");

    // Desenhar valor após o círculo
    doc.setFontSize(8);
    doc.setTextColor(31, 41, 55);
    doc.setFont(undefined, "bold");
    doc.text(prioridadeValue, prioridadeCircleX + 5, yPosition);

    yPosition += lineSpacing;

    addInfoRow("Departamento", ticket.departamento_nome || "N/A", 0, yPosition);
    addInfoRow("Usuário", ticket.usuario_nome || "N/A", 1, yPosition);
    yPosition += lineSpacing;

    addInfoRow(
      "Criado em",
      ticket.created_at ? formatDateSimple(ticket.created_at) : "N/A",
      0,
      yPosition
    );
    if (ticket.updated_at && ticket.updated_at !== ticket.created_at) {
      addInfoRow("Atualizado em", formatDateSimple(ticket.updated_at), 1, yPosition);
    }

    yPosition = boxY + boxHeight;
    yPosition += 7;

    // Título da seção de respostas
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.setFont(undefined, "bold");
    doc.text("Histórico da Conversa", margin, yPosition);
    yPosition += 5;

    // Linha decorativa
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, margin + 50, yPosition);
    yPosition += 6;

    // Buscar todas as respostas do ticket (ordenadas do mais antigo para o mais recente)
    // Converter timeline para formato compatível com respostas para o PDF
    const timelineAsRespostas = timeline.map(event => ({
      id: event.id,
      conteudo: event.descricao,
      usuario_id: event.usuario_id || 1,
      ticket_id: event.ticket_id,
      usuario_nome: event.usuario_nome || 'Sistema',
      created_at: event.created_at,
      updated_at: event.created_at,
    }));
    
    const allRespostasList = [
      ...(ticket.respostas || []),
      ...timelineAsRespostas,
    ];

    // Remover duplicatas e ordenar por data (mais antigas primeiro)
    const uniqueRespostas = allRespostasList.filter(
      (resposta, index, self) => index === self.findIndex((r) => r.id === resposta.id)
    );

    const allRespostas = uniqueRespostas.sort((a: any, b: any) => {
      const ta = new Date(a.created_at || a.createdAt || 0).getTime();
      const tb = new Date(b.created_at || b.createdAt || 0).getTime();
      return ta - tb;
    });

    if (allRespostas.length === 0) {
      addText("Nenhuma resposta ainda.", 10, false, "#6b7280", 4);
    } else {
      allRespostas.forEach((resposta: TicketResposta) => {
        // Verificar se precisa de nova página antes de adicionar resposta
        checkPageBreak(25);

        // Posição inicial do box
        const respostaBoxY = yPosition;

        // Processar conteúdo da resposta (remover HTML tags básicas)
        const content = resposta.conteudo || "";
        const textContent = content
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<p[^>]*>/gi, "\n")
          .replace(/<\/p>/gi, "")
          .replace(/<strong[^>]*>/gi, "")
          .replace(/<\/strong>/gi, "")
          .replace(/<b[^>]*>/gi, "")
          .replace(/<\/b>/gi, "")
          .replace(/<em[^>]*>/gi, "")
          .replace(/<\/em>/gi, "")
          .replace(/<i[^>]*>/gi, "")
          .replace(/<\/i>/gi, "")
          .replace(/<[^>]+>/g, "")
          .replace(/&nbsp;/g, " ")
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .trim();

        // Calcular altura aproximada para o box
        doc.setFontSize(9);
        const estimatedLines = doc.splitTextToSize(
          textContent || "[Sem conteúdo]",
          maxWidth - 10
        );
        const estimatedContentHeight = estimatedLines.length * 4;
        const estimatedAnexosHeight = (resposta.anexos?.length || 0) * 4;
        const estimatedBoxHeight = Math.max(
          45,
          estimatedContentHeight + estimatedAnexosHeight + 35
        );

        // Desenhar box de fundo primeiro (fundo branco)
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, respostaBoxY, maxWidth, estimatedBoxHeight, 2, 2, "FD");

        yPosition += 5;

        // Cabeçalho da resposta
        doc.setFontSize(10);
        doc.setTextColor(31, 41, 55);
        doc.setFont(undefined, "bold");

        const usuarioNome = resposta.usuario_nome || "Usuário Desconhecido";
        const dataFormatada = formatDateSimple(resposta.created_at || "");
        doc.text(usuarioNome, margin + 3, yPosition);

        doc.setFontSize(8);
        doc.setFont(undefined, "normal");
        doc.setTextColor(107, 114, 128);
        const dataWidth = doc.getTextWidth(dataFormatada);
        doc.text(dataFormatada, pageWidth - margin - dataWidth - 3, yPosition);

        yPosition += 5;

        // Linha separadora no cabeçalho
        doc.setDrawColor(229, 231, 235);
        doc.line(margin + 3, yPosition, pageWidth - margin - 3, yPosition);
        yPosition += 5;

        // Conteúdo da resposta
        const contentStartY = yPosition;

        if (textContent && textContent.trim()) {
          doc.setFontSize(9);
          doc.setTextColor(55, 65, 81);
          doc.setFont(undefined, "normal");

          const contentLines = doc.splitTextToSize(textContent, maxWidth - 10);
          contentLines.forEach((line: string) => {
            checkPageBreak(4);
            doc.text(line, margin + 3, yPosition);
            yPosition += 4;
          });
        } else {
          doc.setFontSize(9);
          doc.setTextColor(107, 114, 128);
          doc.setFont(undefined, "italic");
          doc.text("[Sem conteúdo]", margin + 3, yPosition);
          yPosition += 4;
        }

        // Informações de anexos se houver
        if (resposta.anexos && resposta.anexos.length > 0) {
          yPosition += 3;
          doc.setFontSize(8);
          doc.setTextColor(107, 114, 128);
          doc.setFont(undefined, "bold");
          doc.text("Anexos:", margin + 3, yPosition);
          yPosition += 4;
          doc.setFont(undefined, "normal");
          resposta.anexos.forEach((anexo: any) => {
            checkPageBreak(4);
            doc.text(`  • ${anexo.nome || "arquivo"}`, margin + 6, yPosition);
            yPosition += 4;
          });
        }

        // Espaçamento entre respostas
        yPosition += 6;
      });
    }

    // Rodapé em todas as páginas
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      // Linha no rodapé
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

      // Texto do rodapé
      doc.setFontSize(7);
      doc.setTextColor("#9ca3af");
      doc.setFont(undefined, "normal");
      const footerText = `Página ${i} de ${totalPages} | Exportado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}`;
      const footerWidth = doc.getTextWidth(footerText);
      doc.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 6);
    }

    // Salvar o PDF
    const fileName = `Ticket_${ticket.id}_${ticket.assunto?.substring(0, 30).replace(/[^a-z0-9]/gi, "_") || "conversa"}.pdf`;
    doc.save(fileName);

    toast.dismiss("pdf-export");
    toast.success("PDF exportado com sucesso!");
  } catch (error) {
    console.error("Erro ao exportar PDF:", error);
    toast.error("Erro ao exportar PDF");
  }
};

